import { useCallback, useEffect, useState } from 'react';
import type { ChannexRatePlanLink, RatePlan, RoomType, SweepReport, SyncReport } from '@lemontaigu/pms-core';
import { pmsIsChannexId, pmsNormalizeChannexId } from '@lemontaigu/pms-core';
import { CAPABILITIES } from '@lemontaigu/platform';
import { useCapability } from '@lemontaigu/platform-ui';
import { usePms, usePmsScope } from './PmsProvider.js';

// « Correspondance Channex » — which of OUR categories and rate plans each
// Channex one is.
//
// ── Why this screen exists at all ──────────────────────────────────────────
//
// `channex_room_type_id` and `channex_rate_plan_id` have been READ by the
// Channex adapter since P2 and written by NOTHING. `pms_edit_room_type`
// excluded them on purpose — « the mapping belongs to P2 onboarding » — so an
// inbound booking naming a category we cannot resolve fails as a NON-RETRYABLE
// error and dead-letters. Same shape as the Prestations gap: the schema was
// ready, the write path was missing, and the absence was invisible until the
// thing that needed it arrived.
//
// ── No migration was needed, and that is worth recording ───────────────────
//
// Both tables already carry an ALL policy (`pms.room.write` / `pms.rate.write`)
// and kept their grants through the 0100/0101 sweep, so this writes directly
// through PostgREST — the shape `upsertRates`, the room blocks and Prestations
// already use (D4). The gap was a screen and a repository method.
//
// ── « Synchroniser » — the mapping's only ACT ──────────────────────────────
//
// Everything above this screen is a correspondence table; the button is what
// makes it mean something. It publishes our availability and rates for the
// CURRENT établissement and reads them back — the same code path the cron's
// `ari_push` uses, because a manual sync that ran different code would stop
// being evidence about the automatic one.
//
// It lives HERE rather than in its own screen because this is where the
// operator can already see the reason a category would be skipped: an unmapped
// line and « 3 ignorée(s) » in the report are the same fact, one above the
// other. The browser holds no Channex key — the button asks the sync Function,
// through the `channel` port, and never names a URL.
//
// ── UNMAPPED IS A REAL STATE, not an error ─────────────────────────────────
//
// A property is onboarded one category at a time, and half a mapping is normal
// for as long as that takes. So a blank field is `null` and says « non liée »
// rather than complaining — but it says it VISIBLY, because the cost of not
// noticing is a booking that arrives, fails to resolve, and is discovered in a
// dead-letter queue rather than at the desk.

const UUID_HINT = 'UUID Channex, ex. 63fb828f-f34b-4695-83e3-e71afa1f01ab';

interface RowState {
  value: string;
  saving: boolean;
  error: string | null;
  saved: boolean;
}

const initial = (v: string | null): RowState => ({
  value: v ?? '',
  saving: false,
  error: null,
  saved: false,
});

export function ChannexMapping() {
  const pms = usePms();
  // The établissement is read here, unusually for a tab (see PmsProvider's
  // `PmsScope`): a sync must NAME the property it publishes, exactly as
  // provisioning refuses to default it — « synchronise whatever is configured »
  // is one mistake away from publishing one building's availability as
  // another's, and there is no undo on an OTA.
  const { current, loading: scopeLoading } = usePmsScope();
  const canEditRooms = useCapability(CAPABILITIES.PMS_ROOM_WRITE);
  const canEditRates = useCapability(CAPABILITIES.PMS_RATE_WRITE);
  const canSync = useCapability(CAPABILITIES.PMS_OPS_WRITE);

  const [syncing, setSyncing] = useState(false);
  const [report, setReport] = useState<SyncReport | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  const [sweeping, setSweeping] = useState(false);
  const [sweep, setSweep] = useState<SweepReport | null>(null);
  const [sweepError, setSweepError] = useState<string | null>(null);

  const runSync = async () => {
    if (!current) return;
    setSyncing(true);
    setSyncError(null);
    setReport(null);
    const r = await pms.channel.syncNow(current);
    setSyncing(false);
    if (r.ok) setReport(r.value);
    else setSyncError(r.error.message);
  };

  // The desk's answer to « a guest says they booked and I cannot see it ».
  // Deliberately a SEPARATE button from « Synchroniser »: one publishes what we
  // hold, the other fetches what we are missing. They run in opposite
  // directions, and a single « tout synchroniser » would hide which one the
  // operator actually needed — and which one failed.
  const runSweep = async () => {
    if (!current) return;
    setSweeping(true);
    setSweepError(null);
    setSweep(null);
    const r = await pms.channel.sweepNow(current);
    setSweeping(false);
    if (r.ok) setSweep(r.value);
    else setSweepError(r.error.message);
  };

  const [types, setTypes] = useState<RoomType[] | null>(null);
  const [plans, setPlans] = useState<RatePlan[] | null>(null);
  const [links, setLinks] = useState<ChannexRatePlanLink[] | null>(null);
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [t, p, l] = await Promise.all([
      pms.reference.roomTypes(),
      pms.reference.ratePlans(),
      pms.reference.channexRatePlans(),
    ]);
    if (!t.ok) {
      setLoadError(t.error.message);
      return;
    }
    if (!p.ok) {
      setLoadError(p.error.message);
      return;
    }
    if (!l.ok) {
      setLoadError(l.error.message);
      return;
    }
    setTypes(t.value);
    setPlans(p.value);
    setLinks(l.value);
    const next: Record<string, RowState> = {};
    for (const rt of t.value) next[`t${rt.id}`] = initial(rt.channexRoomTypeId);
    // ONE FIELD PER (plan × category), not per plan: Channex requires a rate
    // plan to belong to exactly one room type, so « Tarif standard » is six
    // Channex plans at the hotel (0102).
    for (const rp of p.value) {
      for (const rt of t.value) {
        const found = l.value.find((x) => x.ratePlanId === rp.id && x.roomTypeId === rt.id);
        next[`p${rp.id}:${rt.id}`] = initial(found?.channexRatePlanId ?? null);
      }
    }
    setRows(next);
  }, [pms]);

  useEffect(() => {
    void load();
  }, [load]);

  const patch = (key: string, part: Partial<RowState>) =>
    setRows((r) => ({ ...r, [key]: { ...r[key], ...part } }));

  const save = async (key: string, kind: 'type' | 'plan', id: number, typeId?: number) => {
    const raw = rows[key]?.value ?? '';
    const value = pmsNormalizeChannexId(raw);
    // Checked before the round trip as well as in the service: the likeliest
    // wrong value is the category's NAME, and telling the operator that here is
    // cheaper than a rejected write they have to read an error to understand.
    if (!pmsIsChannexId(value)) {
      patch(key, { error: "Ce n'est pas un UUID Channex", saved: false });
      return;
    }
    patch(key, { saving: true, error: null, saved: false });
    const r =
      kind === 'type'
        ? await pms.reference.setChannexRoomTypeId(id, value)
        : await pms.reference.setChannexRatePlan(id, typeId as number, value);
    if (!r.ok) {
      patch(key, { saving: false, error: r.error.message, saved: false });
      return;
    }
    patch(key, { saving: false, error: null, saved: true, value: value ?? '' });
  };

  if (loadError) return <p className="error">{loadError}</p>;
  if (!types || !plans || !links) return <p className="muted">Chargement…</p>;

  const unmappedTypes = types.filter((t) => !t.channexRoomTypeId).length;
  const unmappedPairs =
    plans.length * types.length -
    links.filter((l) => plans.some((p) => p.id === l.ratePlanId) && types.some((t) => t.id === l.roomTypeId))
      .length;
  const unmapped = unmappedTypes + unmappedPairs;

  const row = (
    key: string,
    kind: 'type' | 'plan',
    id: number,
    code: string,
    label: string,
    editable: boolean,
    typeId?: number,
  ) => {
    const st = rows[key] ?? initial(null);
    return (
      <tr key={key}>
        <td>
          <strong>{code}</strong>
          <div className="muted">{label}</div>
        </td>
        <td>
          <input
            type="text"
            value={st.value}
            placeholder={UUID_HINT}
            disabled={!editable || st.saving}
            spellCheck={false}
            onChange={(e) => patch(key, { value: e.target.value, saved: false, error: null })}
            onBlur={() => void save(key, kind, id, typeId)}
            aria-label={`Identifiant Channex — ${code}`}
          />
          {st.error ? <div className="error">{st.error}</div> : null}
          {st.saved && !st.error ? <div className="muted">Enregistré</div> : null}
          {!st.value && !st.error ? <div className="muted">Non liée</div> : null}
        </td>
      </tr>
    );
  };

  return (
    <section className="channex-mapping">
      <h2 className="section-title">Correspondance Channex</h2>
      <p className="muted">
        Quelle catégorie et quel plan tarifaire de Channex correspond aux nôtres. Une ligne non
        liée n’est pas une erreur — mais une réservation qui arrive pour elle ne peut pas être
        rattachée et part en erreur.
      </p>
      {unmapped > 0 ? (
        <p className="warning">
          {unmapped} ligne{unmapped > 1 ? 's' : ''} non liée{unmapped > 1 ? 's' : ''}.
        </p>
      ) : (
        <p className="muted">Toutes les lignes sont liées.</p>
      )}

      {canSync ? (
        <div className="channex-sync">
          {/* `pms-chip` rather than a bare button: this screen sits inside
              Configuration's shell, and every other control there is a chip
              (docs/UI-BRANDING.md — reuse the surface, never invent a colour). */}
          <button
            type="button"
            className="pms-chip"
            onClick={() => void runSync()}
            disabled={syncing || !current}
          >
            {syncing ? 'Synchronisation…' : 'Synchroniser avec Channex'}
          </button>
          <p className="muted">
            Publie les disponibilités et les tarifs de l’établissement affiché pour l’année à
            venir, puis vérifie ce que Channex en a retenu. Les catégories non liées sont
            ignorées.
          </p>
          {/* An unresolved établissement disables the button rather than
              letting it guess — the message says which of the two states it is
              in, because « le bouton ne marche pas » is not a diagnosis. */}
          {!current && !scopeLoading ? (
            <p className="warning">
              Aucun établissement sélectionné — choisissez-en un pour synchroniser.
            </p>
          ) : null}
          {syncError ? <p className="error">{syncError}</p> : null}
          {report ? <SyncSummary report={report} /> : null}

          <button
            type="button"
            className="pms-chip"
            onClick={() => void runSweep()}
            disabled={sweeping || !current}
          >
            {sweeping ? 'Vérification…' : 'Vérifier les réservations'}
          </button>
          <p className="muted">
            Demande à Channex les réservations qu’il ne nous a pas livrées et les enregistre. À
            utiliser quand un client affirme avoir réservé sans que la réservation apparaisse.
          </p>
          {sweepError ? <p className="error">{sweepError}</p> : null}
          {sweep ? <SweepSummary report={sweep} /> : null}
        </div>
      ) : null}

      <h3>Catégories</h3>
      <table>
        <tbody>
          {types.map((t) => row(`t${t.id}`, 'type', t.id, t.code, t.labelFr, canEditRooms))}
        </tbody>
      </table>

      <h3>Plans tarifaires × catégories</h3>
      <p className="muted">
        Channex exige qu’un plan tarifaire appartienne à UNE catégorie. « {plans[0]?.code ?? 'STD'} »
        correspond donc à {types.length} plans Channex, un par catégorie.
      </p>
      <table>
        <tbody>
          {plans.flatMap((p) =>
            types.map((t) =>
              row(
                `p${p.id}:${t.id}`,
                'plan',
                p.id,
                `${p.code} — ${t.code}`,
                `${p.labelFr} · ${t.labelFr}`,
                canEditRates,
                t.id,
              ),
            ),
          )}
        </tbody>
      </table>
    </section>
  );
}

/**
 * The report, as a person reads it.
 *
 * Deliberately shows the three counts even when two are zero: « 6 publiées »
 * alone cannot be told apart from « 6 publiées, 3 ignorées » by someone who
 * does not already know how many categories exist, and the skipped ones are
 * precisely what this screen is about.
 *
 * `discrepancies === null` prints « non vérifié » rather than a zero. Not
 * checked is not clean, and a zero here would be the more comforting of two
 * different facts.
 */
function SyncSummary({ report }: { report: SyncReport }) {
  const failed = report.roomTypes.filter((o) => o.status === 'failed');
  const skipped = report.roomTypes.filter((o) => o.status === 'skipped');
  return (
    <div className="channex-sync-report">
      <p className={report.failed > 0 ? 'warning' : 'muted'}>
        {report.pushed} catégorie{report.pushed > 1 ? 's' : ''} publiée
        {report.pushed > 1 ? 's' : ''}, {report.skipped} ignorée{report.skipped > 1 ? 's' : ''},{' '}
        {report.failed} en échec — du {report.from} au {report.to}.
      </p>
      <p className="muted">
        Vérification :{' '}
        {report.verifyError
          ? `impossible (${report.verifyError})`
          : report.discrepancies === null
            ? 'non vérifié'
            : `${report.discrepancies} écart${report.discrepancies > 1 ? 's' : ''}`}
        .
      </p>
      {/* CC6 — what the full sync actually sent. A hole in the grid is not
          pushed, so « publiées » must not read as « complet ». */}
      {report.values !== undefined ? (
        <p className="muted">
          {report.values} valeur{report.values > 1 ? 's' : ''} envoyée{report.values > 1 ? 's' : ''}
          {report.taskIds && report.taskIds.length > 0 ? ` — tâche(s) Channex : ${report.taskIds.join(', ')}` : ''}.
        </p>
      ) : null}
      {report.missingCells !== undefined && report.missingCells > 0 ? (
        <p className="warning">
          {report.missingCells} jour{report.missingCells > 1 ? 's' : ''} × plan × catégorie sans tarif dans
          l’horizon — non publiés. Générez la grille des saisons suivantes (Tarifs).
        </p>
      ) : null}
      {failed.length > 0 ? (
        <ul className="error">
          {failed.map((o) => (
            <li key={o.code}>
              <strong>{o.code}</strong> — {o.detail}
            </li>
          ))}
        </ul>
      ) : null}
      {skipped.length > 0 ? (
        <ul className="muted">
          {skipped.map((o) => (
            <li key={o.code}>
              <strong>{o.code}</strong> — {o.detail}
            </li>
          ))}
        </ul>
      ) : null}
      {/* WHICH Channex answered. Sandbox and production ids are
          indistinguishable by shape, and the whole mapping is redone at
          cutover — the same reason the provisioning report carries it. */}
      <p className="muted">Channex : {report.baseUrl || '—'}</p>
    </div>
  );
}

/**
 * The sweep report, as a person reads it.
 *
 * « 0 récupérée » is the GOOD outcome and has to look like it, because this is
 * the button someone presses when they already suspect something is wrong: the
 * honest answer is usually « nothing was missing », and that must not read as a
 * failure.
 *
 * A recovered booking is shown with the tier that caught it. That is not
 * trivia: « file non acquittée » means the webhook was late, while « balayage
 * par date d'arrivée » means the channel had already stopped offering it — the
 * second says the webhook has been broken for a while, not for a moment.
 */
function SweepSummary({ report }: { report: SweepReport }) {
  const failed = report.revisions.filter((r) => r.status === 'failed');
  const nothing = report.applied === 0 && report.failed === 0 && report.ackFailed === 0;
  return (
    <div className="channex-sweep-report">
      <p className={report.failed > 0 || report.stalled ? 'warning' : 'muted'}>
        {nothing
          ? 'Aucune réservation manquante — tout ce que Channex a vendu est déjà ici.'
          : `${report.applied} réservation${report.applied > 1 ? 's' : ''} récupérée${report.applied > 1 ? 's' : ''}, ${report.failed} en échec.`}
      </p>
      <p className="muted">
        {report.offered} en attente d’acquittement, {report.scanned} examinée
        {report.scanned > 1 ? 's' : ''} par date d’arrivée.
        {report.collectionError ? ` Balayage impossible : ${report.collectionError}` : ''}
      </p>
      {report.applied > 0 ? (
        <ul className="muted">
          {report.revisions
            .filter((r) => r.status !== 'failed')
            .map((r) => (
              <li key={r.revisionId}>
                <strong>{r.channelRef}</strong> —{' '}
                {r.source === 'feed' ? 'file non acquittée' : 'balayage par date d’arrivée'}
                {r.status === 'ackFailed' ? ' (enregistrée, acquittement à refaire)' : ''}
              </li>
            ))}
        </ul>
      ) : null}
      {failed.length > 0 ? (
        <ul className="error">
          {failed.map((r) => (
            <li key={r.revisionId}>
              <strong>{r.channelRef}</strong> — {r.detail}
            </li>
          ))}
        </ul>
      ) : null}
      {/* A stuck feed is the one state that does not resolve itself: Channex
          keeps offering a booking we cannot apply, and every later sweep will
          meet it again until the cause is fixed. */}
      {report.stalled ? (
        <p className="warning">
          Channex continue de proposer une réservation que nous ne pouvons pas enregistrer —
          corrigez la cause ci-dessus, elle sera reprise au prochain essai.
        </p>
      ) : null}
    </div>
  );
}
